<script setup lang="ts">
  import { ref, computed, watch, type PropType } from 'vue'
  import { MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
  import InputField from '@/components/admin/form/InputField.vue'
  import type { CustomerForm, UnifiedDocumentTemplate } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import { debounce } from '@/lib/timing'

  // Type for documents that have id and title (both CustomerForm and UnifiedDocumentTemplate)
  type DocumentTemplate = CustomerForm | UnifiedDocumentTemplate

  const { t } = useTranslation({
    en: {
      documentsTemplate: 'Documents Template',
      searchDocuments: 'Search Documents',
      selectAll: 'Select All',
      selected: 'selected',
      page: 'Page',
      of: 'of'
    },
    de: {
      documentsTemplate: 'Dokumentenvorlage',
      searchDocuments: 'Dokumentenvorlage suchen',
      selectAll: 'Alle auswählen',
      selected: 'ausgewählt',
      page: 'Seite',
      of: 'von'
    }
  })

  const props = defineProps({
    forms: {
      type: Array as PropType<DocumentTemplate[]>,
      default: () => []
    },
    selectedFormIds: {
      type: Array as PropType<CustomerForm['id'][]>,
      default: () => []
    },
    currentPage: {
      type: Number,
      default: 1
    },
    totalItems: {
      type: Number,
      default: 0
    },
    itemsPerPage: {
      type: Number,
      default: 10
    }
  })

  const emit = defineEmits(['update:selectedDocuments', 'page-change', 'search-change'])

  const searchQuery = ref('')
  const currentPageInput = ref(String(props.currentPage))

  const selectedFormIds = computed(() => new Set(props.selectedFormIds))
  const selectedCount = computed(() => selectedFormIds.value.size)
  const isAllSelected = computed(() => {
    return selectedCount.value === props.forms.length && props.forms.length > 0
  })
  const totalPages = computed(() => Math.ceil(props.totalItems / props.itemsPerPage))

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages.value) {
      currentPageInput.value = String(newPage)
      emit('page-change', newPage)
    }
  }

  const toggleAllDocuments = () => {
    const newSelectedIds = isAllSelected.value
      ? []
      : props.forms.map((form: DocumentTemplate) => form.id)
    emit('update:selectedDocuments', newSelectedIds)
  }

  const toggleDocument = (id: string) => {
    const newSelectedIds = new Set(selectedFormIds.value)
    if (newSelectedIds.has(id)) {
      newSelectedIds.delete(id)
    } else {
      newSelectedIds.add(id)
    }
    emit('update:selectedDocuments', Array.from(newSelectedIds))
  }

  const debouncedEmitSearch = debounce((query: string) => {
    emit('search-change', query)
  }, 300)

  watch(searchQuery, (newValue) => {
    debouncedEmitSearch(newValue)
  })
</script>

<template>
  <div
    data-cy="document-template-section"
    class="mt-6 border border-gray-300 bg-white shadow sm:rounded-lg"
  >
    <div class="p-4">
      <h3 class="mb-2 text-lg font-semibold text-gray-900">{{ t('documentsTemplate') }}</h3>
      <div class="relative mb-2">
        <InputField
          v-model="searchQuery"
          data-cy="document-search-input"
          name="document-search"
          :placeholder="t('searchDocuments')"
        >
          <template #append>
            <MagnifyingGlassIcon
              class="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400"
            />
          </template>
        </InputField>
      </div>
      <div class="flex items-center justify-between pl-2">
        <label class="flex items-center">
          <input
            data-cy="document-select-all"
            type="checkbox"
            :checked="isAllSelected"
            class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            @change="toggleAllDocuments"
          />
          <span class="ml-2 text-sm font-medium text-gray-400">{{ t('selectAll') }}</span>
        </label>
        <span
          data-cy="document-selected-count"
          class="text-sm text-gray-500"
        >
          {{ selectedCount }} {{ t('selected') }}
        </span>
      </div>
      <div class="border-t border-gray-200"></div>
      <div class="max-h-60 overflow-y-auto pl-2">
        <template
          v-for="(form, index) in props.forms"
          :key="form.id"
        >
          <div
            :data-cy="`document-item-${form.id}`"
            class=""
          >
            <label class="flex items-center">
              <input
                :data-cy="`document-checkbox-${form.id}`"
                type="checkbox"
                :checked="selectedFormIds.has(form.id)"
                class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                @change="toggleDocument(form.id)"
              />
              <span class="ml-2 text-sm font-medium text-gray-700">
                {{ form.title }}
              </span>
            </label>
          </div>
          <div
            v-if="index < props.forms.length - 1"
            class="border-t border-gray-200"
          ></div>
        </template>
      </div>
      <div class="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
        <div class="flex items-center text-sm">
          <span class="text-gray-700">{{ t('page') }}</span>
          <input
            v-model="currentPageInput"
            type="text"
            class="mx-2 w-12 rounded-md border-gray-300 p-1 text-center text-sm"
            @change="handlePageChange(Number(currentPageInput))"
          />
          <span class="text-gray-700">{{ t('of') }} {{ totalPages }}</span>
        </div>
        <div class="flex items-center space-x-2">
          <button
            :disabled="props.currentPage === 1"
            class="rounded p-1 hover:bg-gray-100 disabled:opacity-50"
            @click="handlePageChange(props.currentPage - 1)"
          >
            <ChevronLeftIcon class="h-5 w-5 text-gray-500" />
          </button>
          <button
            :disabled="props.currentPage === totalPages"
            class="rounded p-1 hover:bg-gray-100 disabled:opacity-50"
            @click="handlePageChange(props.currentPage + 1)"
          >
            <ChevronRightIcon class="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
